import L from 'leaflet'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import type { FeatureCollection } from 'geojson'
import { BASEMAPS, buildLocationsLayer, buildOgdpLayers, buildSchoolsLayer, buildSpillsLayer, buildTanksLayer, buildWellsLayer, createHelpModal, createLegend, ogdpPadInfo } from './MapUtils'

;(globalThis as Record<string, unknown>).L = L

await import('leaflet-hash')
await import('leaflet-easybutton')
await import('leaflet.fullscreen')
await import('leaflet-textpath')

const markerIcon2x = new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href
const markerIcon = new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href
const markerShadow = new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow })

const EMPTY_FEATURE_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] }

// Parse OGDP id from URL: /maps/ogdp/century[/...]
function getOgdpId(): string | null {
  const match = window.location.pathname.match(/\/maps\/ogdp\/([a-z0-9_-]+)/i)
  return match ? match[1] : null
}

// Locations/spills/tanks/wells files are generated separately (see
// scripts/get_ogdp_data.sh) and may not exist yet for every OGDP — treat a
// missing file as an empty layer rather than failing the whole page.
async function fetchOptionalGeoJSON(url: string): Promise<FeatureCollection> {
  try {
    const res = await fetch(url)
    if (!res.ok) return EMPTY_FEATURE_COLLECTION
    return await res.json() as FeatureCollection
  } catch {
    return EMPTY_FEATURE_COLLECTION
  }
}

export async function initMap(): Promise<L.Map | null> {
  const ogdpId = getOgdpId()

  if (!ogdpId) {
    console.log('No OGDP id found in URL. Expected: /ogdp/century')
    return null
  }

  const base = import.meta.env.BASE_URL

  let ogdpGeoJSON: FeatureCollection
  try {
    const res = await fetch(`${base}data/${ogdpId}.geojson`)
    if (!res.ok) throw new Error(`OGDP "${ogdpId}" not found`)
    ogdpGeoJSON = await res.json()
  } catch (err) {
    console.log(err)
    document.getElementById('error')!.textContent = (err as Error).message
    return null
  }

  const [locationsGeoJSON, spillsGeoJSON, tanksGeoJSON, wellsGeoJSON, schoolsGeoJSON] = await Promise.all([
    fetchOptionalGeoJSON(`${base}data/${ogdpId}_locations.geojson`),
    fetchOptionalGeoJSON(`${base}data/${ogdpId}_spills.geojson`),
    fetchOptionalGeoJSON(`${base}data/${ogdpId}_tanks.geojson`),
    fetchOptionalGeoJSON(`${base}data/${ogdpId}_wells.geojson`),
    fetchOptionalGeoJSON(`${base}data/${ogdpId}_schools.geojson`),
  ])

  const { name: ogdpName } = ogdpPadInfo(ogdpGeoJSON)
  document.title = `${ogdpName} — EcoCarto OGDP Map`

  const map = L.map('map', { zoomControl: true, zoomSnap: 0.5 })

  const ogdpLayers = buildOgdpLayers(ogdpGeoJSON, map)
  const ogdpFeatureGroup = L.featureGroup(Object.values(ogdpLayers)).addTo(map)
  const bounds = ogdpFeatureGroup.getBounds()
  map.fitBounds(bounds)

  const locationsLayer = buildLocationsLayer(locationsGeoJSON).addTo(map)
  const tanksLayer = buildTanksLayer(tanksGeoJSON).addTo(map)
  const spillsLayer = buildSpillsLayer(spillsGeoJSON).addTo(map)
  const wellsLayer = buildWellsLayer(wellsGeoJSON).addTo(map)
  const schoolsLayer = buildSchoolsLayer(schoolsGeoJSON).addTo(map)

  createLegend([
    ...(ogdpLayers.dsu.getLayers().length ? [{ label: 'Drilling & Spacing Unit', type: 'polygon' as const, color: '#cc0000', fillColor: 'rgba(204,0,0,0.03)', weight: 3, dashArray: '10,4' }] : []),
    { label: 'Proposed Pad', type: 'polygon', color: '#8d5a99', fillColor: 'rgba(141,90,153,0.6)', weight: 2, dashArray: '6,6' },
    ...(ogdpLayers.altLocations.getLayers().length ? [{ label: `Alternate Locations (${ogdpLayers.altLocations.getLayers().length})`, type: 'polygon' as const, color: '#c6adcc', fillColor: 'rgba(198,173,204,0.3)', weight: 2, dashArray: '3,3' }] : []),
    { label: `Proposed Wellbores (${ogdpLayers.wellbores.getLayers().length})`, type: 'line', color: '#baa0f8', weight: 2 },
    { label: '2,000 ft Buffer', type: 'circle', color: '#cc0000', fillColor: 'rgba(204,0,0,0.1)', weight: 2 },
    { label: '1 Mile Buffer', type: 'circle', color: '#ff7f00', fillColor: 'rgba(255,127,0,0.1)', weight: 2 },
    { label: '5 km Buffer (~3.1 mi)', type: 'circle', color: '#1f78b4', fillColor: 'rgba(31,120,180,0.1)', weight: 2 },
    ...(schoolsGeoJSON.features.length ? [{ label: `Schools (${schoolsGeoJSON.features.length.toLocaleString()})`, svg: `${base}school.svg` }] : []),
    ...(locationsGeoJSON.features.length ? [{ label: `Locations 10+ Wells (${locationsGeoJSON.features.length.toLocaleString()})`, type: 'circle' as const, color: '#000', fillColor: 'rgba(0,0,0,0.4)', weight: 1 }] : []),
    ...(tanksGeoJSON.features.length ? [{ label: `Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`, svg: `${base}tank.svg` }] : []),
    ...(spillsGeoJSON.features.length ? [{ label: `Spills (${spillsGeoJSON.features.length.toLocaleString()})`, type: 'polygon' as const, color: '#ff1493', fillColor: 'rgba(255,105,180,0.2)', weight: 1, rotation: 45 }] : []),
    ...(wellsGeoJSON.features.length ? [{ label: `Wells (${wellsGeoJSON.features.length.toLocaleString()})`, type: 'circle' as const, color: '#c00', fillColor: 'rgba(204,0,0,0.4)', weight: 1 }] : []),
  ], 'bottomleft').addTo(map)

  const logoControl = L.Control.extend({
    options: { position: 'bottomleft' },
    onAdd() {
      const container = L.DomUtil.create('div')
      L.DomEvent.disableClickPropagation(container)
      container.innerHTML = `<a href="https://ecocarto.com/" target="_blank"><img src="${base}logo.png" style="display:block;height:auto;width:180px;"></a>`
      return container
    },
  })
  new logoControl().addTo(map)

  L.easyButton('fa-solid fa-house', (_, map) => map.fitBounds(bounds)).addTo(map)
  createHelpModal(map, ogdpName, ogdpLayers.altLocations.getLayers().length > 0).addTo(map)
  new L.Hash(map)
  // @ts-expect-error — leaflet.fullscreen v5 type not yet updated
  new L.Control.FullScreen({ position: 'topleft', forceSeparateButton: false }).addTo(map)

  // Reference point for "distance to pad" in search results: the proposed
  // pad's centroid, falling back to the overall bounds center.
  const padBounds = ogdpLayers.pad.getBounds()
  const padReferencePoint = padBounds.isValid() ? padBounds.getCenter() : bounds.getCenter()

  // @ts-expect-error — GeoSearchControl types are loose
  map.addControl(new GeoSearchControl({
    provider: new OpenStreetMapProvider(),
    style: 'bar',
    showMarker: true,
    showPopup: true,
    popupFormat: ({ result }: { result: { x: number; y: number; label: string } }) => {
      const miles = (L.latLng(result.y, result.x).distanceTo(padReferencePoint) * 0.000621371).toFixed(2)
      return `<b>${result.label}</b><br>Distance to Pad: ${miles} mi`
    },
    // We fit the bounds ourselves (see geosearch/showlocation below) so the
    // pad stays in view alongside the result, instead of just centering on it.
    updateMap: false,
    retainZoomLevel: false,
    autoClose: true,
    keepResult: true,
  }))

  map.on('geosearch/showlocation', (e: unknown) => {
    const { location } = e as { location: { x: number; y: number } }
    const resultLatLng = L.latLng(location.y, location.x)
    map.fitBounds(L.latLngBounds([padBounds.getSouthWest(), padBounds.getNorthEast(), resultLatLng]), { padding: [40, 40] })
  })

  BASEMAPS['CartoDB Positron'].addTo(map)
  L.control.layers(
    BASEMAPS,
    {
      ...(ogdpLayers.dsu.getLayers().length ? { 'Drilling & Spacing Unit': ogdpLayers.dsu } : {}),
      'Proposed Pad': ogdpLayers.pad,
      ...(ogdpLayers.altLocations.getLayers().length ? { [`Alternate Locations (${ogdpLayers.altLocations.getLayers().length})`]: ogdpLayers.altLocations } : {}),
      [`Proposed Wellbores (${ogdpLayers.wellbores.getLayers().length})`]: ogdpLayers.wellbores,
      '2,000 ft Buffer': ogdpLayers.buffer2000ft,
      '1 Mile Buffer': ogdpLayers.buffer1mi,
      '5 km Buffer (~3.1 mi)': ogdpLayers.buffer5km,
      ...(schoolsGeoJSON.features.length ? { [`Schools (${schoolsGeoJSON.features.length.toLocaleString()})`]: schoolsLayer } : {}),
      ...(locationsGeoJSON.features.length ? { [`Locations (${locationsGeoJSON.features.length.toLocaleString()})`]: locationsLayer } : {}),
      ...(spillsGeoJSON.features.length ? { [`Spills (${spillsGeoJSON.features.length.toLocaleString()})`]: spillsLayer } : {}),
      ...(tanksGeoJSON.features.length ? { [`Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`]: tanksLayer } : {}),
      ...(wellsGeoJSON.features.length ? { [`Wells (${wellsGeoJSON.features.length.toLocaleString()})`]: wellsLayer } : {}),
    },
    { position: 'topleft', collapsed: true }
  ).addTo(map)

  return map
}
